// "From site" at the top of an area's extra rows (SCHEMA.md "Before UAT" §1,
// §3): what the measurer ticked. Preliminaries are flagged until their line
// has a rate — the line added for them, whatever it's renamed to (#418); a tag that suggests hire offers the matching items,
// one tap to add — never added on its own (DD, 2026-10-13).
import { AlertTriangle, Check, Plus } from 'lucide-react';
import { AddAction } from './add-action';
import { addAreaLine, deskRequirements, hireMatches, prelimLine, readSiteReqs, type AccessTag, type AreaChoice, type OptionScope, type Prelim, type TickSource } from '@higgins/m2q-pricing';
import { useDeviceQuery } from '../lib/hooks';
import { loadAccessTags } from '../lib/reference';
import type { HireItemFromApi } from '../lib/types';
import { formatMoney } from './surface-format';
import { COLUMN_COUNT, PINNED_ROW } from './take-off-rows';

interface Props {
  areaId: string;
  /** The area's ticks as stored: its rows (or, before it has any, hig_sitereqs). */
  siteReqs: TickSource;
  choice: AreaChoice | undefined;
  hireItems: readonly HireItemFromApi[];
  onScope: (change: (scope: OptionScope) => OptionScope) => void | Promise<void>;
}

function readRequirements(siteReqs: TickSource, tags: readonly AccessTag[]) {
  try {
    return readSiteReqs(siteReqs, tags);
  } catch {
    return null;
  }
}

/** A preliminary: priced once its line has a rate; otherwise one tap adds that line. */
function PrelimNeed({ prelim, choice, onAdd }: { prelim: Prelim; choice: AreaChoice | undefined; onAdd: () => void }) {
  const { label } = prelim;
  const line = prelimLine(prelim, choice?.otherTrades ?? []);
  if (line && line.rate > 0) {
    return (
      <span className="flex items-center gap-1 text-green-800">
        <Check className="h-3.5 w-3.5" /> {label}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-amber-800">
      <AlertTriangle className="h-3.5 w-3.5" /> {label} — {line ? 'needs a rate' : 'not priced'}
      {!line && (
        <AddAction onClick={onAdd} className="ml-1">
          Add as line
        </AddAction>
      )}
    </span>
  );
}

export function TakeOffSiteNeeds({ areaId, siteReqs, choice, hireItems, onScope }: Props) {
  const tags = useDeviceQuery(loadAccessTags, []);
  if (!tags) return null;
  const doc = readRequirements(siteReqs, tags);
  if (!doc) return null;
  const needs = deskRequirements(doc, tags);
  if (!needs.access.length && !needs.prelims.length) return null;
  const onLine = new Set((choice?.access ?? []).map((l) => l.hireItemId).filter(Boolean));

  const addPrelim = (p: Prelim) => onScope((s) => addAreaLine(s, areaId, 'otherTrades', { description: p.label, qty: 1, rate: 0, siteItem: p.key }));
  const addHire = (id: string) => id && onScope((s) => addAreaLine(s, areaId, 'access', { hireItemId: id, days: 1, trips: 1 }));

  return (
    <tr className="bg-amber-50/40">
      <td colSpan={COLUMN_COUNT} className="px-2 py-1.5 text-sm">
        <div className={`${PINNED_ROW} flex flex-wrap items-center gap-x-4 gap-y-1`}>
          <span className="text-xs font-semibold uppercase text-slate-500">From site</span>
          {needs.access.length > 0 && <span className="text-slate-700">{needs.access.join(' · ')}</span>}
          {needs.prelims.map((p) => (
            <PrelimNeed key={p.key} prelim={p} choice={choice} onAdd={() => addPrelim(p)} />
          ))}
          {needs.hire.map(({ tag, search }) => {
            const matches = hireMatches(hireItems, search);
            if (!matches.length) return null;
            const added = matches.some((m) => onLine.has(m.id));
            return (
              <label key={tag} className="flex items-center gap-1">
                {added ? <Check className="h-3.5 w-3.5 text-green-700" /> : <Plus className="h-3.5 w-3.5 text-primary" />}
                <select value="" onChange={(e) => addHire(e.target.value)} aria-label={`Hire for ${tag}`} className="h-7 rounded-md border border-slate-300 bg-white px-1 text-sm text-slate-700">
                  <option value="">{added ? `${tag}: add another…` : `${tag}: pick a size…`}</option>
                  {matches.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name} — {formatMoney(h.dayRate)}/day
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
        </div>
      </td>
    </tr>
  );
}
